import { CircularProgress } from "@material-ui/core";
import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../../_metronic/_partials/controls";
import { getCareersLogsAsync, getWebCareersCityListAsync } from "../../redux";
import "./careersSnapshotView.scss";

const statusLabelMap = {
  draft: "Draft",
  published: "Published",
  pending_review: "Pending Review",
  future: "Future",
  auto_draft: "Auto Draft",
};
const getStatusLabel = (status) => {
  if (status && statusLabelMap[status]) {
    return statusLabelMap[status];
  }
  return status;
};

// Read-only "label above value" field, laid out like the Edit Careers
// page's form-group blocks - this page is a point-in-time viewer, not a form.
const Field = ({ label, children, className, style }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <div
      className={`form-control re_inputRouded${className ? ` ${className}` : ""}`}
      style={{ minHeight: 42, ...style }}
    >
      {children ?? <span className="text-muted">-</span>}
    </div>
  </div>
);

const CareersSnapshotView = () => {
  const { careersId, logId, which } = useParams();
  const dispatch = useDispatch();
  const { isLogsLoading, careersLogs, categoryList } = useSelector(
    (state) => state.webcluescareers,
    shallowEqual
  );

  useEffect(() => {
    if (careersId) {
      dispatch(getCareersLogsAsync(careersId));
    }
    dispatch(getWebCareersCityListAsync());
  }, [careersId, dispatch]);

  const log = careersLogs?.find((l) => String(l._id) === String(logId));
  const data = log ? (which === "old" ? log.oldData : log.newData) : null;
  const heading = which === "old" ? "Old Careers" : "New Careers";

  // Resolve the stored city id to its name, falling back to the raw id if
  // the city was later deleted.
  const resolveCityName = (id) => {
    if (!id) return null;
    const match = (categoryList || []).find(
      (city) => String(city._id) === String(id)
    );
    return match ? match.label : id;
  };

  if (isLogsLoading) {
    return (
      <div style={{ flex: "0 0 auto" }}>
        <div style={{ justifyContent: "center" }} className="text-center">
          <div className="basic-verification-loader text-center">
            <CircularProgress />
          </div>
        </div>
      </div>
    );
  }

  if (!log || !data) {
    return (
      <Card>
        <CardHeader
          title={
            <div className="form-group m-0 d-flex align-items-center">
              <Link
                className="btn btn-link px-0"
                to={`/webclues_careers_log/${careersId}`}
              >
                <SVG
                  className="h-50 align-self-center"
                  src={toAbsoluteUrl(
                    "/media/svg/icons/Navigation/Arrow-left.svg"
                  )}
                />
                Back
              </Link>
              <span className="pl-2">
                Webclues Careers - {heading} Snapshot
                {log?.name ? ` - ${log.name}` : ""}
              </span>
            </div>
          }
        >
          <CardHeaderToolbar>
            <Link
              className="btn btn-outline-blue"
              to={`/webclues_careers_log/${careersId}`}
            >
              Close
            </Link>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <div className="text-center py-5">
            {!log
              ? "History entry not found."
              : `No ${which === "old" ? "before" : "after"} data recorded for this action.`}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Row>
      <Col md={8}>
        <Card>
          <CardHeader
            title={
              <div className="form-group m-0 d-flex align-items-center">
                <Link
                  className="btn btn-link px-0"
                  to={`/webclues_careers_log/${careersId}`}
                >
                  <SVG
                    className="h-50 align-self-center"
                    src={toAbsoluteUrl(
                      "/media/svg/icons/Navigation/Arrow-left.svg"
                    )}
                  />
                  Back
                </Link>
                <span className="pl-2">
                  Webclues Careers - {heading} Snapshot
                  {data?.title ? ` - ${data.title}` : ""}
                </span>
              </div>
            }
          ></CardHeader>
          <CardBody>
            <Row className="justify-content-center">
              <Col lg={12}>
                <Field label="Slug">{data.slug}</Field>
                <Field label="Title">{data.title}</Field>
                <Field label="Experience">{data.experience}</Field>
                <Field
                  label="Description"
                  className="careers-description-preview"
                  style={{
                    height: "auto",
                    maxHeight: 400,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: data.description || "" }}
                  />
                </Field>
                <div className="text-right">
                  <Link
                    className="btn btn-outline-blue"
                    to={`/webclues_careers_log/${careersId}`}
                  >
                    Close
                  </Link>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>

      <Col md={4}>
        <Card>
          <CardHeader
            title={
              <div className="form-group m-0 d-flex align-items-center">
                <span className="pl-2">Careers Attribute</span>
              </div>
            }
          ></CardHeader>
          <CardBody>
            <Field label="City">{resolveCityName(data.category)}</Field>
            <Field label="Created Date">
              {data.createdOn
                ? new Date(data.createdOn).toLocaleString()
                : null}
            </Field>
            <Field label="Updated On">
              {data.updatedOn
                ? new Date(data.updatedOn).toLocaleString()
                : null}
            </Field>
            <Field label="Status">
              <div style={{ textTransform: "capitalize" }}>
                {getStatusLabel(data.status)}
              </div>
            </Field>
            <Field label="Deleted">{data.isDeleted ? "Yes" : "No"}</Field>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default CareersSnapshotView;
